'use client'

// The job centre, in the town (sim-server world/JobCentre.js, docs/job-centre.md). Sam, 30 Oct: "i don't want the jobs
// listing on their own page, I want them in the world as an overlay at the job center. We should make it themed like Job
// center too". Walk up to it (what was the tech shop, on the street south of the pizza place) and a button takes you in:
// the camera goes across the street to its window (JobCentreCam) and the board opens beside it, the vending machine's
// way (VendingHud). ?jobs on the address opens it from anywhere, which is where /jobs goes.
//
// Inside: the vacancies he has put up, pinned to a cork board, each with its pay and his note. Sam, 30 Oct: "instead of
// take this job, we have see this job, that opens up more details on it. Then, take this job shows them the quick terms
// of it and they have to click to accept the job. This will generate a page for them, which we also email out to them".
// So: See this job, then Take this job (the terms and where to send the page), then Accept, and the job is theirs with
// its own light page (/job/<code>, JobPage), which is where the picture is taken: the world and a phone's camera at
// once is too much for a phone. The jobs filled lately, with the picture, who did it and his line about it. Your jobs:
// the ones they've taken, what they're owed, and claiming it (YourJobs, also at /jobs/mine).

import { useCallback, useEffect, useState } from 'react'
import useGameStore from '../../stores/gameStore'
import { authFetch, useAccount } from '../auth/useUser'
import { jobCentreSpot } from '../../lib/world/jobCentre'
import { look, Sign, Btn, IndexCard, Stamp, Board, Label, Small, Led, field } from './look'
import { YourJobs, left, whenAt, jobPage, money } from './YourJobs'
import { Pics } from './Pics'

const NEAR = 7
const POLL_MS = 450

// a card's tilt, from its id, so it stays put between reads
const tiltOf = (id) => ((String(id).split('').reduce((n, c) => n + c.charCodeAt(0), 0) % 5) - 2) * 0.6
// who a job is for, by the rank it needs (lib/tiers.js names, earned at the shrine)
// how many more people can take a job: a job has places, fewer for the dearer ones (Sam, 31 Oct)
const placesLeft = (p) => (!p?.slots ? '' : p.slots > 1 ? `${p.left} of ${p.slots} places left` : 'One place')
const FOR = { 'A new face': 'Anyone signed in', 'Someone he knows': 'Anyone he knows', 'A familiar face': 'Familiar faces and up', 'One of his own': 'One of his own only' }
// a price in SOL at the town's rate for the day (Sam, 31 Oct: "can we get a currency converter on the jobs board to switch
// between GBP and SOL...default to GBP"). what is paid is still pounds, shop credit or SOL as they choose
const solOf = (pence, gbpPerSol) => `${(pence / 100 / gbpPerSol).toFixed(4)} SOL`
const CURRENCY_KEY = 'jobs-currency'
function savedCurrency() {
  try { return typeof window !== 'undefined' && window.localStorage.getItem(CURRENCY_KEY) === 'SOL' ? 'SOL' : 'GBP' } catch { return 'GBP' }
}

const INTRO = 'Pino has saved up enough money to get on a train, but without a plan. So he is enlisting the help of humans to do tasks for him to give him a real world experience of trains.'

export function JobCentreHud() {
  const [spot] = useState(() => (typeof window === 'undefined' ? null : jobCentreSpot()))
  const [near, setNear] = useState(false)
  const open = useGameStore((s) => s.jobsOpen)
  const setOpen = useGameStore((s) => s.setJobsOpen)
  const { signedIn, ready } = useAccount()
  const [board, setBoard] = useState(null)
  const [mine, setMine] = useState(null)
  const [tab, setTab] = useState('board')
  const [busy, setBusy] = useState('')
  const [said, setSaid] = useState(null)
  const [now, setNow] = useState(() => Date.now())
  // one vacancy looked at: { id, step: 'see' | 'take' | 'done', token?, email? }
  const [view, setView] = useState(null)
  const [email, setEmail] = useState('')
  const [cur, setCur] = useState(savedCurrency)
  const pickCur = (c) => { setCur(c); try { window.localStorage.setItem(CURRENCY_KEY, c) } catch { /* fine without */ } }

  // is it open at all: the board says, once, and again whenever the overlay closes
  useEffect(() => { fetch('/api/jobs', { cache: 'no-store' }).then((r) => r.json()).then(setBoard).catch(() => {}) }, [open])

  // near its door, the walker's way (VendingHud)
  useEffect(() => {
    if (!spot) return
    const t = setInterval(() => {
      const pos = useGameStore.getState().playerPosGetter?.()
      if (!pos) { setNear(false); return }
      setNear(Math.hypot(pos.x - spot.stand[0], pos.z - spot.stand[2]) < NEAR)
    }, POLL_MS)
    return () => clearInterval(t)
  }, [spot])

  const pull = useCallback(async () => {
    const [b, m] = await Promise.all([
      fetch('/api/jobs', { cache: 'no-store' }).then((r) => r.json()).catch(() => null),
      authFetch('/api/jobs/me', { cache: 'no-store' }).then((r) => r.json()).catch(() => null),
    ])
    if (b) setBoard(b)
    if (m) setMine(m)
    setNow(Date.now())
  }, [])
  useEffect(() => {
    if (!open) return
    pull()
    const t = setInterval(pull, 20000)
    const c = setInterval(() => setNow(Date.now()), 15000)
    return () => { clearInterval(t); clearInterval(c) }
  }, [open, pull, signedIn])

  const go = useCallback(() => {
    try { document.exitPointerLock?.() } catch { /* not locked */ }
    setOpen(true)
  }, [setOpen])
  const close = useCallback(() => {
    setOpen(false)
    setSaid(null)
    setView(null)
    if (typeof window !== 'undefined' && new URLSearchParams(window.location.search).has('jobs')) {
      const u = new URL(window.location.href)
      u.searchParams.delete('jobs')
      window.history.replaceState(null, '', u.toString())
    }
  }, [setOpen])

  // ?jobs opens it from anywhere (and /jobs comes here)
  useEffect(() => { if (new URLSearchParams(window.location.search).has('jobs')) go() }, [go])
  useEffect(() => {
    if (!open) return
    const onKey = (e) => { if (e.key === 'Escape') close() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, close])

  const act = async (key, url, body) => {
    setBusy(key); setSaid(null)
    try {
      const res = await authFetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body || {}) })
      const j = await res.json().catch(() => null)
      if (!res.ok || j?.ok === false) setSaid({ bad: true, text: j?.message || 'That didn\'t work. Try again.' })
      return j
    } finally { setBusy(''); pull() }
  }
  const signIn = () => { const st = useGameStore.getState(); st.setAuthWanted?.(true); setTimeout(() => st.authApi?.login?.(), 300) }
  const see = (id) => { setSaid(null); setView({ id, step: 'see' }) }
  const toTerms = () => { setSaid(null); setEmail((e) => e || mine?.email || ''); setView((v) => ({ ...v, step: 'take' })) }
  const accept = async () => {
    const j = await act('accept', '/api/jobs/take', { post: view.id, email: email.trim(), agreed: true })
    if (j?.ok) setView((v) => ({ ...v, step: 'done', token: j.claim?.token, email: email.trim(), dueAt: j.claim?.dueAt }))
  }
  const toTab = (id) => { setTab(id); setView(null); setSaid(null) }

  if (!spot) return null
  const isOpen = !!board?.open
  if (!open) {
    if (!near || !isOpen) return null
    return (
      <div style={{ position: 'fixed', left: '50%', bottom: 132, transform: 'translateX(-50%)', zIndex: 40 }}>
        <button onClick={go} style={{ background: look.lime, color: '#1f3a05', border: `2px solid ${look.limeDeep}`, borderBottom: `4px solid ${look.yellow}`, borderRadius: 2, padding: '11px 18px', fontFamily: look.sans, fontWeight: 900, letterSpacing: '0.08em', textTransform: 'uppercase', cursor: 'pointer', boxShadow: '0 3px 0 rgba(0,0,0,0.3)' }}>
          Go into the job centre
        </button>
      </div>
    )
  }

  // (opened from ?jobs before the board is read: nothing yet; and shut, it says so)
  if (!board) return null
  if (!board.open) {
    return (
      <div style={{ position: 'fixed', inset: 0, zIndex: 60, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
        <div style={{ pointerEvents: 'auto', width: 'min(360px, calc(100vw - 32px))', background: look.wall, color: look.ink, border: `3px solid ${look.signDeep}` }}>
          <Sign />
          <div style={{ padding: 14 }}>
            <Small>The job centre isn't open yet.</Small>
            <div style={{ marginTop: 10 }}><Btn primary onClick={close}>Leave</Btn></div>
          </div>
        </div>
      </div>
    )
  }

  const claim = mine?.claim
  const can = mine?.canTake
  const tabs = [['board', 'Vacancies'], ['filled', 'Filled'], ['mine', 'Your jobs']]
  const rate = board.solGbp || null
  const inSol = cur === 'SOL' && !!rate
  const fmt = (pence, label) => (inSol && Number.isFinite(pence) ? solOf(pence, rate) : label || money(pence || 0))
  const looked = view ? board.board?.find((p) => p.id === view.id) || null : null

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 60, pointerEvents: 'none' }}>
      <style>{`
        .jc-panel { color: ${look.ink}; position: absolute; right: 24px; top: 24px; bottom: 24px; width: 430px; pointer-events: auto; display: flex; flex-direction: column; background: ${look.wall}; box-shadow: 0 10px 40px rgba(0,0,0,0.45); border: 3px solid ${look.signDeep}; }
        .jc-body { overflow-y: auto; flex: 1; padding: 14px; }
        @media (max-width: 640px) { .jc-panel { left: 8px; right: 8px; top: auto; bottom: 8px; width: auto; height: min(640px, calc(100dvh - 24px)); } }
      `}</style>
      <div className="jc-panel" role="dialog" aria-label="The job centre">
        <div style={{ position: 'relative' }}>
          <Sign sub={`${fmt(board.money?.left, board.money?.label)} left for jobs · paid from Pino's train money`} />
          <button onClick={close} aria-label="Leave" style={{ position: 'absolute', right: 10, top: 12, background: look.sign, border: '2px solid #fff', color: '#fff', width: 30, height: 30, borderRadius: 2, fontWeight: 900, cursor: 'pointer', fontFamily: look.sans }}>×</button>
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: '8px 14px', background: look.wallDeep, borderBottom: `2px solid ${look.lime}` }}>
          <div style={{ display: 'flex', gap: 4, flexWrap: 'nowrap' }}>
            {tabs.map(([id, name]) => (
              <button key={id} onClick={() => toTab(id)} style={{ padding: '6px 9px', border: `2px solid ${look.sign}`, borderRadius: 2, background: tab === id ? look.yellow : '#fff', color: look.sign, fontFamily: look.sans, fontWeight: 800, fontSize: 11, letterSpacing: '0.06em', textTransform: 'uppercase', whiteSpace: 'nowrap', cursor: 'pointer' }}>{name}</button>
            ))}
          </div>
          <div role="group" aria-label="Show prices in" style={{ display: 'flex', flexWrap: 'nowrap' }}>
            {['GBP', 'SOL'].map((c) => (
              <button key={c} onClick={() => pickCur(c)} disabled={c === 'SOL' && !rate} aria-pressed={cur === c} title={c === 'SOL' && !rate ? 'The SOL price isn\'t in yet' : `Show prices in ${c}`} style={{ padding: '6px 8px', border: `2px solid ${look.sign}`, marginLeft: c === 'SOL' ? -2 : 0, borderRadius: 2, background: (c === 'SOL' ? inSol : !inSol) ? look.sign : '#fff', color: (c === 'SOL' ? inSol : !inSol) ? '#fff' : look.sign, fontFamily: look.sans, fontWeight: 800, fontSize: 11, letterSpacing: '0.06em', whiteSpace: 'nowrap', cursor: c === 'SOL' && !rate ? 'default' : 'pointer', opacity: c === 'SOL' && !rate ? 0.5 : 1 }}>{c}</button>
            ))}
          </div>
          <Led label="Done today" value={String(board.counts?.doneToday ?? 0).padStart(3, '0')} />
        </div>
        <div className="jc-body">
          {said && <div style={{ padding: '9px 11px', marginBottom: 12, border: `2px solid ${said.bad ? look.stamp : look.sign}`, background: '#fff', fontFamily: look.sans, fontWeight: 700, fontSize: 13, color: said.bad ? look.stamp : look.sign }}>{said.text}</div>}

          {tab === 'board' && view && (
            <JobLooked
              view={view} post={looked} board={board} mine={mine} can={can} claim={claim} busy={busy} email={email} setEmail={setEmail} fmt={fmt}
              onTake={toTerms} onAccept={accept} onBack={() => { setView(null); setSaid(null) }} onSignIn={signIn}
            />
          )}

          {tab === 'board' && !view && (
            <>
              <Small style={{ marginBottom: 12, fontSize: 13.5, color: look.ink }}>{INTRO}</Small>
              {inSol && <Small style={{ marginBottom: 12 }}>Prices in SOL at today's rate, 1 SOL to £{Number(rate).toFixed(2)}. You choose how you're paid: shop credit, a UK bank transfer or SOL.</Small>}

              {claim && (
                <div style={{ background: '#fff', color: look.ink, border: `2px dashed ${look.ink}`, padding: 12, marginBottom: 14, fontFamily: look.type }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <div style={{ fontFamily: look.sans, fontWeight: 900, letterSpacing: '0.1em', fontSize: 12 }}>YOUR JOB · {fmt(claim.job?.pence, claim.job?.label)}</div>
                    <Led label="Left" value={left(claim.dueAt - now)} />
                  </div>
                  <div style={{ fontSize: 16, fontWeight: 700, margin: '8px 0 4px' }}>{claim.job?.ask || claim.job?.text}</div>
                  <Small>Take the picture on your job page, on your phone. Send it by {whenAt(claim.dueAt)}, or your place goes back on the board.</Small>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 10 }}>
                    {claim.token && <Btn small primary href={jobPage(claim.token)} target="_blank">Open your job page</Btn>}
                    <Btn small disabled={!!busy} onClick={() => act('drop', '/api/jobs/drop')}>Give it back</Btn>
                  </div>
                </div>
              )}

              {ready && !signedIn && !mine?.signedIn && (
                <div style={{ background: '#fff', border: `2px solid ${look.sign}`, padding: 12, marginBottom: 14 }}>
                  <div style={{ fontFamily: look.sans, fontWeight: 800, fontSize: 14 }}>Sign in to take a job.</div>
                  <Small>Anyone signed in can take one, a job at a time.</Small>
                  <div style={{ marginTop: 8 }}><Btn primary onClick={signIn}>Sign in</Btn></div>
                </div>
              )}
              {mine?.signedIn && !claim && can && !can.ok && <Small style={{ marginBottom: 12, color: look.stamp, fontWeight: 700 }}>{can.why}</Small>}

              {!board.board?.length ? (
                <Board><div style={{ background: look.card, padding: 14, fontFamily: look.type, textAlign: 'center' }}>No vacancies right now. Pino puts more up through the day.</div></Board>
              ) : (
                <Board>
                  {board.board.map((p) => (
                    <IndexCard key={p.id} tilt={tiltOf(p.id)} faded={p.state !== 'open'}>
                      {p.state !== 'open' && <Stamp>TAKEN</Stamp>}
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, fontFamily: look.sans, fontSize: 10, fontWeight: 800, letterSpacing: '0.12em', textTransform: 'uppercase', color: look.inkSoft }}>
                        <span style={p.dear ? { color: look.stamp } : undefined}>{p.dear ? 'Top pay' : 'Vacancy'} · {p.chapter}</span><span style={{ color: look.ink, fontSize: p.dear ? 16 : 13 }}>{fmt(p.pence, p.label)}</span>
                      </div>
                      <div style={{ fontSize: 15.5, fontWeight: 700, lineHeight: 1.35, margin: '12px 0 6px' }}>{p.ask || p.text}</div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginTop: 10 }}>
                        <span style={{ fontFamily: look.sans, fontSize: 11, color: look.inkSoft }}>{p.state === 'open' ? [placesLeft(p), FOR[p.need]].filter(Boolean).join(' · ') : p.until ? `A place may come back ${/^\d/.test(whenAt(p.until)) ? 'at ' : ''}${whenAt(p.until)}` : 'Being checked'}</span>
                        {p.state === 'open' && <Btn small primary onClick={() => see(p.id)}>See this job</Btn>}
                      </div>
                    </IndexCard>
                  ))}
                </Board>
              )}
              <details style={{ marginTop: 14 }}>
                <summary style={{ fontFamily: look.sans, fontWeight: 800, fontSize: 12, letterSpacing: '0.08em', textTransform: 'uppercase', cursor: 'pointer', color: look.sign }}>The rules</summary>
                {board.rules?.map((r) => <Small key={r}>· {r}</Small>)}
              </details>
            </>
          )}

          {tab === 'filled' && (
            <>
              <Small style={{ marginBottom: 12 }}>What people have brought back for him, and what he made of it.</Small>
              {!board.done?.length && <Small>Nothing filled yet.</Small>}
              <div style={{ display: 'grid', gap: 16 }}>
                {board.done?.map((d) => (
                  <div key={d.id} style={{ background: '#fff', padding: '10px 10px 12px', boxShadow: '0 3px 10px rgba(0,0,0,0.2)', transform: `rotate(${tiltOf(d.id) / 2}deg)` }}>
                    <Pics urls={d.urls?.length ? d.urls : [d.url]} alt={d.text} />
                    <div style={{ fontFamily: look.type, fontSize: 13.5, fontWeight: 700, marginTop: 8 }}>{d.text}</div>
                    <Small>{fmt(d.pence, d.label)} · by {d.name} · {whenAt(d.at)}</Small>
                    {d.hisLine && <div style={{ fontFamily: look.type, fontSize: 13.5, fontStyle: 'italic', marginTop: 6, color: look.sign }}>Pino: “{d.hisLine}”</div>}
                  </div>
                ))}
              </div>
            </>
          )}

          {tab === 'mine' && (
            !mine?.signedIn ? (
              <div style={{ background: '#fff', border: `2px solid ${look.sign}`, padding: 12 }}>
                <div style={{ fontFamily: look.sans, fontWeight: 800, fontSize: 14 }}>Sign in to see your jobs and your pay.</div>
                <div style={{ marginTop: 8 }}><Btn primary onClick={signIn}>Sign in</Btn></div>
              </div>
            ) : (
              <>
                <YourJobs mine={mine} holdHours={board.holdHours} payTimes={board.payTimes} onChanged={pull} now={now} />
                <div style={{ marginTop: 14 }}><a href="/jobs/mine" target="_blank" rel="noopener" style={{ fontFamily: look.sans, fontWeight: 800, fontSize: 12.5, color: look.sign }}>Your jobs on a page of their own</a></div>
              </>
            )
          )}
        </div>
      </div>
    </div>
  )
}

export default JobCentreHud

/** One vacancy looked at: what it is (See this job), the terms and where to send its page (Take this job), and then
 * the job is yours, with its page and the email. */
function JobLooked({ view, post, board, mine, can, claim, busy, email, setEmail, fmt, onTake, onAccept, onBack, onSignIn }) {
  const back = <Btn small onClick={onBack}>Back to the vacancies</Btn>
  if (view.step === 'done') {
    return (
      <div style={{ background: '#fff', border: `2px solid ${look.sign}`, padding: 14 }}>
        <div style={{ fontFamily: look.sans, fontWeight: 900, fontSize: 18 }}>The job is yours.</div>
        <Small style={{ marginTop: 6 }}>We've emailed your job page to {view.email}. Open it on your phone: that's where you take the picture and send it{view.dueAt ? `, by ${whenAt(view.dueAt)}` : ''}.</Small>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 12 }}>
          {view.token && <Btn primary href={jobPage(view.token)} target="_blank">Open your job page</Btn>}
          {back}
        </div>
      </div>
    )
  }
  if (!post) {
    return (
      <div style={{ background: '#fff', border: `2px solid ${look.sign}`, padding: 14 }}>
        <Small style={{ marginBottom: 10 }}>That job has come off the board.</Small>
        {back}
      </div>
    )
  }
  const taken = post.state !== 'open'
  const where = [post.where, post.abroad ? 'outside the UK' : '', post.onTrain ? "only if you're on a train anyway" : ''].filter(Boolean).join(' · ')
  const card = (
    <Board>
      <IndexCard tilt={0} faded={taken}>
        {taken && <Stamp>TAKEN</Stamp>}
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, fontFamily: look.sans, fontSize: 10, fontWeight: 800, letterSpacing: '0.12em', textTransform: 'uppercase', color: look.inkSoft }}>
          <span>Vacancy · {post.chapter}</span><span style={{ color: look.ink, fontSize: 14 }}>{fmt(post.pence, post.label)}</span>
        </div>
        <div style={{ fontSize: 17, fontWeight: 700, lineHeight: 1.35, margin: '12px 0 6px' }}>{post.ask || post.text}</div>
        {where && <div style={{ fontSize: 13 }}>{where}</div>}
        {post.note && <div style={{ fontSize: 13.5, fontStyle: 'italic', margin: '6px 0 0' }}>Pino says: “{post.note}”</div>}
      </IndexCard>
    </Board>
  )

  if (view.step === 'take') {
    return (
      <>
        {card}
        <div style={{ background: '#fff', border: `1.5px solid ${look.inkSoft}`, padding: 12, marginTop: 14 }}>
          <div style={{ fontFamily: look.sans, fontWeight: 900, fontSize: 13, letterSpacing: '0.08em', textTransform: 'uppercase' }}>Before you take it</div>
          {board.terms?.map((t) => <Small key={t} style={{ marginTop: 5 }}>· {t}</Small>)}
          <Label>Where to send your job page</Label>
          <input style={field} type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" placeholder="you@example.com" />
          <Small style={{ marginTop: 8 }}>Accepting means you agree to these terms.</Small>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 10 }}>
            <Btn primary disabled={!!busy || taken || !email.trim()} onClick={onAccept}>{busy === 'accept' ? '...' : 'Accept the job'}</Btn>
            {back}
          </div>
        </div>
      </>
    )
  }

  return (
    <>
      {card}
      <div style={{ background: '#fff', border: `1.5px solid ${look.inkSoft}`, padding: 12, marginTop: 14 }}>
        <Small style={{ color: look.ink }}>For: {FOR[post.need] || post.need}. Pays {fmt(post.pence, post.label)}{post.slots > 1 ? ` to each person who does it. ${placesLeft(post)}.` : '.'}</Small>
        <Small style={{ marginTop: 6 }}>Once you take it you have {board.claimHours} hours. You get a word to write on paper, and you take the picture with your phone on the job's own page, which we email to you. Every picture is checked within {board.checkHours || 24} hours. Pay can be claimed {board.holdHours} hours after it passes, as shop credit, a UK bank transfer or SOL.</Small>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 12 }}>
          {taken ? <Small style={{ color: look.stamp, fontWeight: 700 }}>Every place on it is taken.</Small>
            : !mine?.signedIn ? <Btn primary onClick={onSignIn}>Sign in to take it</Btn>
              : claim ? <Small style={{ color: look.stamp, fontWeight: 700 }}>You have a job on already.</Small>
                : can && !can.ok ? <Small style={{ color: look.stamp, fontWeight: 700 }}>{can.why}</Small>
                  : <Btn primary disabled={!!busy} onClick={onTake}>Take this job</Btn>}
          {back}
        </div>
      </div>
    </>
  )
}
